import { useMemo } from 'react';
import gmailIcon from '../assets/gmail_icon.png';
import { useCurrentUserProfile } from '../hook/useCurrentUserProfile';
import { EmailPhonePreview } from './EmailPhonePreview';

export { EmailPhonePreview };

export function EmailTemplatePreview(props) {
  const { variant = 'phone' } = props;
  if (variant === 'desktop') {
    return <EmailDesktopPreview {...props} />;
  }
  return <EmailPhonePreview {...props} />;
}

export function EmailDesktopPreview({
  template,
  subject: customSubject,
  body: customBody,
  senderName: customSenderName,
  senderEmail: customSenderEmail,
  recipientEmail = '{email}',
  headerTitle = 'Email Follow-Up Preview',
  showFooterBadge = true,
  footerBadgeText = 'Delivers directly to Gmail inbox in same thread',
  className = '',
}) {
  const { companyName: profileCompanyName } = useCurrentUserProfile();

  const companyName = useMemo(() => {
    return (
      customSenderName ||
      profileCompanyName ||
      localStorage.getItem('company_name') ||
      'flexion infotech'
    );
  }, [customSenderName, profileCompanyName]);

  const senderEmail = useMemo(() => {
    if (customSenderEmail) return customSenderEmail;
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const u = JSON.parse(storedUser);
        if (u.email) return u.email;
      }
    } catch {
      // Ignore
    }
    return 'info@company.com';
  }, [customSenderEmail]);

  const formattedDateStr = useMemo(() => {
    try {
      return new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Oct 8, 03:47 PM';
    }
  }, []);

  const displaySubject = useMemo(() => {
    if (customSubject && customSubject.trim()) return customSubject;
    if (template?.subject && template.subject.trim()) return template.subject;
    return 'Following up on our recent conversation';
  }, [customSubject, template]);

  const displayBody = useMemo(() => {
    if (customBody && customBody.trim()) return customBody;
    if (template?.body && template.body.trim()) return template.body;
    return `<p>Hi <strong>KRISH</strong>,</p><p>I hope this email finds you well.<br>I'm reaching out to follow up on our recent conversation. Please let me know if you have any questions or need further information.</p><p>Looking forward to your response.<br>Best regards,<br><strong>Ridham Talaviya</strong></p>`;
  }, [customBody, template]);

  const avatarInitial = (companyName || 'F').charAt(0).toUpperCase();

  const isHtmlBody =
    typeof displayBody === 'string' &&
    displayBody.includes('<') &&
    displayBody.includes('>');

  return (
    <div
      className={`w-full bg-white rounded-2xl border border-gray-200/90 shadow-md p-4 sm:p-5 space-y-3 text-left font-sans ${className}`}
    >
      {/* Header Title Bar — Gmail icon + label */}
      {headerTitle && (
        <div className="flex items-center gap-2 border-b border-gray-100 pb-3">
          <img
            src={gmailIcon}
            alt="Gmail"
            className="w-5 h-5 object-contain shrink-0"
          />
          <span className="font-bold text-gray-900 text-sm leading-none">
            {headerTitle}
          </span>
        </div>
      )}

      {/* Email Window Mockup */}
      <div className="w-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden text-left">
        {/* Window Control Dots */}
        <div className="bg-gray-100/90 px-3.5 py-2.5 border-b border-gray-200/80 flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F56] inline-block" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E] inline-block" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#27C93F] inline-block" />
        </div>

        {/* Sender row: Avatar | [Name + icons] / [to email + date] */}
        <div className="px-3 sm:px-4 py-3 border-b border-gray-100 flex items-start gap-2.5 bg-white">
          {/* Avatar */}
          <div className="w-8 h-8 rounded-full bg-[#4285F4] text-white font-bold text-xs flex items-center justify-center shrink-0 uppercase shadow-sm mt-0.5">
            {avatarInitial}
          </div>

          {/* Name + recipient stacked, fills remaining width */}
          <div className="flex-1 min-w-0">
            {/* Row 1: Company name left, action icons right */}
            <div className="flex items-center justify-between gap-1">
              <span
                className="font-bold text-xs sm:text-sm text-gray-900 truncate"
                title={`${companyName} <${senderEmail}>`}
              >
                {companyName}
              </span>

              <div className="flex items-center gap-2 shrink-0 text-gray-400 ml-2">
                {/* Star */}
                <button
                  type="button"
                  className="hover:text-gray-600 transition-colors"
                  title="Star"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                    />
                  </svg>
                </button>
                {/* Reply */}
                <button
                  type="button"
                  className="hover:text-gray-600 transition-colors"
                  title="Reply"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                    />
                  </svg>
                </button>
                {/* More */}
                <button
                  type="button"
                  className="hover:text-gray-600 transition-colors"
                  title="More actions"
                >
                  <svg
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Row 2: "to recipient" left, date right */}
            <div className="flex items-center justify-between gap-1 mt-0.5">
              <div className="flex items-center gap-0.5 min-w-0 text-[11px] text-gray-500">
                <span className="truncate font-medium">
                  to {recipientEmail}
                </span>
                <svg
                  className="w-3 h-3 text-gray-400 shrink-0 cursor-pointer hover:text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
              <span className="text-gray-400 text-[10px] sm:text-[11px] font-medium whitespace-nowrap shrink-0">
                {formattedDateStr}
              </span>
            </div>
          </div>
        </div>

        {/* Email Subject */}
        <div className="px-3 sm:px-4 py-2.5 border-b border-gray-100 bg-white">
          <h3 className="text-xs sm:text-sm font-bold text-gray-900 leading-snug break-words">
            {displaySubject}
          </h3>
        </div>

        {/* Email Body */}
        <div className="p-3 sm:p-5 bg-white text-xs sm:text-sm text-gray-700 leading-relaxed min-h-[140px] max-h-[380px] overflow-y-auto">
          <div className="p-3 sm:p-4 rounded-xl border border-gray-100 bg-[#FAFAFA]/70 min-h-[90px]">
            {isHtmlBody ? (
              <div
                className="prose prose-sm max-w-none text-gray-700 break-words [overflow-wrap:anywhere]"
                dangerouslySetInnerHTML={{ __html: displayBody }}
              />
            ) : (
              <div className="whitespace-pre-wrap text-gray-700 font-normal break-words [overflow-wrap:anywhere]">
                {displayBody}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer delivery badge */}
      {showFooterBadge && footerBadgeText && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200/60 p-2.5 rounded-lg font-medium flex items-center gap-1.5">
          <span>✓</span> {footerBadgeText}
        </div>
      )}
    </div>
  );
}
